import jsPDF from 'jspdf';

interface Course {
  title: string;
  description: string;
  niche: string;
  targetAudience?: string;
  learningObjectives?: string[];
  modules: Module[];
}

interface Module {
  title: string;
  description: string;
  objectives?: string[];
  lessons: Lesson[];
  quiz?: Quiz;
}

interface Lesson {
  title: string;
  description: string;
  duration: number;
  contentType: string;
  content?: {
    introduction?: string;
    mainContent?: string;
    keyTakeaways?: string[];
    exercises?: string[];
    videoScript?: string;
    resources?: string[];
  };
}

interface Quiz {
  questions: Array<{
    question: string;
    options: string[];
    correctAnswer: string;
    explanation: string;
  }>;
}

export const exportCourseToPDF = (course: Course) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;

  // Line height multiplier for better readability
  const lineHeightMultiplier = 0.45;

  // Helper function to add new page if needed
  const checkPageBreak = (neededHeight: number = 15) => {
    if (yPosition + neededHeight > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Helper function to wrap text
  const wrapText = (text: string, maxWidth: number, fontSize: number): string[] => {
    pdf.setFontSize(fontSize);
    return pdf.splitTextToSize(text, maxWidth);
  };

  // Helper function to calculate height needed for wrapped text
  const calculateTextHeight = (text: string, fontSize: number): number => {
    const lines = wrapText(text, contentWidth, fontSize);
    return lines.length * fontSize * lineHeightMultiplier;
  };

  // Helper function to add text with wrapping
  const addText = (text: string, fontSize: number, isBold: boolean = false, color: [number, number, number] = [0, 0, 0]) => {
    if (!text) return;

    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
    pdf.setTextColor(color[0], color[1], color[2]);

    const lines = wrapText(text, contentWidth, fontSize);
    const lineHeight = fontSize * lineHeightMultiplier;
    const totalHeight = lines.length * lineHeight;

    // Check if we need a page break for the entire text block
    // If text is very long, we'll handle it line by line instead
    if (totalHeight < pageHeight - 2 * margin) {
      checkPageBreak(totalHeight);
    }

    lines.forEach(line => {
      // Always check before each line for very long text
      if (yPosition + lineHeight > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }
      pdf.text(line, margin, yPosition);
      yPosition += lineHeight;
    });
    pdf.setTextColor(0, 0, 0); // Reset to black
  };

  // Cover Page
  pdf.setFillColor(99, 102, 241); // Indigo color
  pdf.rect(0, 0, pageWidth, 80, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(32);
  pdf.setFont('helvetica', 'bold');
  const titleLines = wrapText(course.title, contentWidth, 32);
  yPosition = 30;
  titleLines.forEach(line => {
    pdf.text(line, margin, yPosition, { align: 'left' });
    yPosition += 12;
  });

  // Brand name
  yPosition = 60;
  pdf.setFontSize(14);
  pdf.text('Powered by Templimize AI', margin, yPosition);

  yPosition = 100;
  pdf.setTextColor(0, 0, 0);

  // Course Description
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Course Overview', margin, yPosition);
  yPosition += 10;

  addText(course.description, 12, false);
  yPosition += 10;

  // Course Details
  if (course.niche) {
    addText(`Niche: ${course.niche}`, 11, true, [99, 102, 241]);
    yPosition += 8;
  }

  if (course.targetAudience) {
    addText(`Target Audience: ${course.targetAudience}`, 11, true, [139, 92, 246]);
    yPosition += 8;
  }

  // Learning Objectives
  if (course.learningObjectives && course.learningObjectives.length > 0) {
    checkPageBreak(40);
    yPosition += 5;
    addText('Learning Objectives', 16, true);
    yPosition += 5;

    course.learningObjectives.forEach((obj, idx) => {
      checkPageBreak();
      addText(`${idx + 1}. ${obj}`, 11, false);
      yPosition += 5;
    });
  }

  // Modules
  course.modules.forEach((module, moduleIdx) => {
    checkPageBreak(40);
    yPosition += 10;

    // Module Header
    pdf.setFillColor(243, 244, 246); // Light gray background
    pdf.rect(margin - 5, yPosition - 7, contentWidth + 10, 15, 'F');

    addText(`Module ${moduleIdx + 1}: ${module.title}`, 16, true, [99, 102, 241]);
    yPosition += 5;

    addText(module.description, 11, false);
    yPosition += 8;

    // Module Objectives
    if (module.objectives && module.objectives.length > 0) {
      addText('Module Objectives:', 12, true);
      yPosition += 3;

      module.objectives.forEach(obj => {
        checkPageBreak();
        addText(`- ${obj}`, 10, false);
        yPosition += 4;
      });
      yPosition += 5;
    }

    // Lessons
    module.lessons.forEach((lesson, lessonIdx) => {
      checkPageBreak(30);
      yPosition += 5;

      addText(`Lesson ${lessonIdx + 1}: ${lesson.title}`, 14, true, [139, 92, 246]);
      yPosition += 3;

      addText(`${lesson.description} (${lesson.duration} min - ${lesson.contentType})`, 10, false, [107, 114, 128]);
      yPosition += 5;

      // Lesson Content
      if (lesson.content) {
        if (lesson.content.introduction) {
          checkPageBreak(15);
          addText('Introduction:', 11, true);
          yPosition += 3;
          addText(lesson.content.introduction, 10, false);
          yPosition += 5;
        }

        if (lesson.content.mainContent) {
          checkPageBreak(20);
          addText('Content:', 11, true);
          yPosition += 3;
          addText(lesson.content.mainContent, 10, false);
          yPosition += 5;
        }

        if (lesson.content.keyTakeaways && lesson.content.keyTakeaways.length > 0) {
          checkPageBreak(15);
          addText('Key Takeaways:', 11, true);
          yPosition += 3;
          lesson.content.keyTakeaways.forEach(takeaway => {
            checkPageBreak();
            addText(`- ${takeaway}`, 10, false);
            yPosition += 4;
          });
          yPosition += 3;
        }

        if (lesson.content.exercises && lesson.content.exercises.length > 0) {
          checkPageBreak(15);
          addText('Exercises:', 11, true);
          yPosition += 3;
          lesson.content.exercises.forEach((exercise, idx) => {
            checkPageBreak();
            addText(`${idx + 1}. ${exercise}`, 10, false);
            yPosition += 4;
          });
          yPosition += 3;
        }

        if (lesson.content.resources && lesson.content.resources.length > 0) {
          checkPageBreak(15);
          addText('Resources:', 11, true);
          yPosition += 3;
          lesson.content.resources.forEach(resource => {
            checkPageBreak();
            addText(`- ${resource}`, 10, false);
            yPosition += 4;
          });
          yPosition += 3;
        }

        if (lesson.content.videoScript) {
          checkPageBreak(20);
          addText('Video Script:', 11, true);
          yPosition += 3;
          addText(lesson.content.videoScript, 9, false);
          yPosition += 5;
        }
      }

      yPosition += 3;
    });

    // Module Quiz
    if (module.quiz && module.quiz.questions.length > 0) {
      checkPageBreak(40);
      yPosition += 10;

      pdf.setFillColor(219, 234, 254); // Light blue background
      pdf.rect(margin - 5, yPosition - 7, contentWidth + 10, 12, 'F');

      addText(`Module ${moduleIdx + 1} Quiz`, 14, true, [37, 99, 235]);
      yPosition += 5;

      module.quiz.questions.forEach((q, qIdx) => {
        checkPageBreak(30);
        yPosition += 5;

        addText(`Q${qIdx + 1}: ${q.question}`, 11, true);
        yPosition += 4;

        q.options.forEach((option, oIdx) => {
          checkPageBreak();
          const isCorrect = option.startsWith(q.correctAnswer);
          addText(
            `${String.fromCharCode(65 + oIdx)}. ${option}${isCorrect ? ' (correct)' : ''}`,
            10,
            isCorrect,
            isCorrect ? [34, 197, 94] : [0, 0, 0]
          );
          yPosition += 4;
        });

        yPosition += 2;
        addText(`Explanation: ${q.explanation}`, 9, false, [107, 114, 128]);
        yPosition += 5;
      });
    }
  });

  // Footer on last page
  checkPageBreak(30);
  yPosition = pageHeight - 30;
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 8;

  addText('Generated by Templimize AI - The Ultimate AI Course Builder', 10, false, [107, 114, 128]);
  addText(`Created on: ${new Date().toLocaleDateString()}`, 9, false, [156, 163, 175]);

  // Save the PDF
  const fileName = `${course.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_course.pdf`;
  pdf.save(fileName);
};

// Helper function to escape HTML entities
const escapeHtml = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Helper function to convert newlines to <br> tags
const nl2br = (text: string): string => {
  if (!text) return '';
  return escapeHtml(text).replace(/\n/g, '<br>');
};

export const exportCourseToWord = (course: Course) => {
  // Build HTML parts separately to avoid nested template literal issues
  const parts: string[] = [];

  // Head section
  parts.push(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(course.title)}</title>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; max-width: 800px; margin: 40px auto; padding: 20px; }
    h1 { color: #6366f1; border-bottom: 3px solid #6366f1; padding-bottom: 10px; }
    h2 { color: #8b5cf6; margin-top: 30px; }
    h3 { color: #ec4899; margin-top: 20px; }
    .module { background: #f9fafb; padding: 20px; margin: 20px 0; border-left: 4px solid #8b5cf6; }
    .lesson { margin: 15px 0; padding: 15px; background: white; border: 1px solid #e5e7eb; }
    .quiz { background: #dbeafe; padding: 15px; margin: 20px 0; border-radius: 8px; }
    .correct { color: #22c55e; font-weight: bold; }
    ul, ol { margin: 10px 0; }
    li { margin: 5px 0; }
    .meta { color: #6b7280; font-size: 0.9em; }
    .section { margin: 15px 0; }
  </style>
</head>
<body>`);

  // Title and overview
  parts.push(`<h1>${escapeHtml(course.title)}</h1>`);
  parts.push(`<p><strong>Powered by Templimize AI</strong></p>`);
  parts.push(`<h2>Course Overview</h2>`);
  parts.push(`<p>${nl2br(course.description)}</p>`);

  if (course.niche) {
    parts.push(`<p class="meta"><strong>Niche:</strong> ${escapeHtml(course.niche)}</p>`);
  }
  if (course.targetAudience) {
    parts.push(`<p class="meta"><strong>Target Audience:</strong> ${escapeHtml(course.targetAudience)}</p>`);
  }

  // Learning objectives
  if (course.learningObjectives && course.learningObjectives.length > 0) {
    parts.push(`<h2>Learning Objectives</h2>`);
    parts.push(`<ul>`);
    course.learningObjectives.forEach(obj => {
      parts.push(`<li>${escapeHtml(obj)}</li>`);
    });
    parts.push(`</ul>`);
  }

  // Modules
  course.modules.forEach((module, moduleIdx) => {
    parts.push(`<div class="module">`);
    parts.push(`<h2>Module ${moduleIdx + 1}: ${escapeHtml(module.title)}</h2>`);
    parts.push(`<p>${nl2br(module.description)}</p>`);

    // Module objectives
    if (module.objectives && module.objectives.length > 0) {
      parts.push(`<h3>Module Objectives</h3>`);
      parts.push(`<ul>`);
      module.objectives.forEach(obj => {
        parts.push(`<li>${escapeHtml(obj)}</li>`);
      });
      parts.push(`</ul>`);
    }

    // Lessons
    module.lessons.forEach((lesson, lessonIdx) => {
      parts.push(`<div class="lesson">`);
      parts.push(`<h3>Lesson ${lessonIdx + 1}: ${escapeHtml(lesson.title)}</h3>`);
      parts.push(`<p class="meta">${escapeHtml(lesson.description)} (${lesson.duration} min - ${escapeHtml(lesson.contentType)})</p>`);

      if (lesson.content) {
        if (lesson.content.introduction) {
          parts.push(`<div class="section"><strong>Introduction:</strong><br>${nl2br(lesson.content.introduction)}</div>`);
        }
        if (lesson.content.mainContent) {
          parts.push(`<div class="section"><strong>Content:</strong><br>${nl2br(lesson.content.mainContent)}</div>`);
        }
        if (lesson.content.keyTakeaways && lesson.content.keyTakeaways.length > 0) {
          parts.push(`<div class="section"><strong>Key Takeaways:</strong></div>`);
          parts.push(`<ul>`);
          lesson.content.keyTakeaways.forEach(t => {
            parts.push(`<li>${escapeHtml(t)}</li>`);
          });
          parts.push(`</ul>`);
        }
        if (lesson.content.exercises && lesson.content.exercises.length > 0) {
          parts.push(`<div class="section"><strong>Exercises:</strong></div>`);
          parts.push(`<ol>`);
          lesson.content.exercises.forEach(e => {
            parts.push(`<li>${escapeHtml(e)}</li>`);
          });
          parts.push(`</ol>`);
        }
        if (lesson.content.resources && lesson.content.resources.length > 0) {
          parts.push(`<div class="section"><strong>Resources:</strong></div>`);
          parts.push(`<ul>`);
          lesson.content.resources.forEach(r => {
            parts.push(`<li>${escapeHtml(r)}</li>`);
          });
          parts.push(`</ul>`);
        }
        if (lesson.content.videoScript) {
          parts.push(`<div class="section"><strong>Video Script:</strong><br><small>${nl2br(lesson.content.videoScript)}</small></div>`);
        }
      }

      parts.push(`</div>`); // Close lesson
    });

    // Module quiz
    if (module.quiz && module.quiz.questions.length > 0) {
      parts.push(`<div class="quiz">`);
      parts.push(`<h3>Module ${moduleIdx + 1} Quiz</h3>`);

      module.quiz.questions.forEach((q, qIdx) => {
        parts.push(`<p><strong>Q${qIdx + 1}: ${escapeHtml(q.question)}</strong></p>`);
        parts.push(`<ul>`);
        q.options.forEach((option, oIdx) => {
          const isCorrect = option.startsWith(q.correctAnswer);
          const className = isCorrect ? ' class="correct"' : '';
          const checkmark = isCorrect ? ' ✓' : '';
          parts.push(`<li${className}>${String.fromCharCode(65 + oIdx)}. ${escapeHtml(option)}${checkmark}</li>`);
        });
        parts.push(`</ul>`);
        parts.push(`<p class="meta"><em>Explanation: ${escapeHtml(q.explanation)}</em></p>`);
      });

      parts.push(`</div>`); // Close quiz
    }

    parts.push(`</div>`); // Close module
  });

  // Footer
  parts.push(`<hr>`);
  parts.push(`<p class="meta">Generated by Templimize AI - The Ultimate AI Course Builder<br>Created on: ${new Date().toLocaleDateString()}</p>`);
  parts.push(`</body></html>`);

  // Join all parts
  const htmlContent = parts.join('\n');

  // Create a Blob and download as HTML (compatible with Google Docs import)
  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${course.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_course.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface CertificateOptions {
  studentName: string;
  courseName: string;
  completionDate: string;
  instructorName?: string;
  brandName?: string;
  brandColor?: string;
}

export const generateCertificate = (options: CertificateOptions) => {
  const {
    studentName,
    courseName,
    completionDate,
    instructorName = 'Course Instructor',
    brandName = 'Templimize AI',
    brandColor = '#6366f1'
  } = options;

  const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // Background gradient effect (using rectangles)
  pdf.setFillColor(99, 102, 241); // Indigo
  pdf.rect(0, 0, pageWidth, 25, 'F');
  pdf.rect(0, pageHeight - 25, pageWidth, 25, 'F');

  // Decorative border
  pdf.setDrawColor(99, 102, 241);
  pdf.setLineWidth(3);
  pdf.rect(15, 15, pageWidth - 30, pageHeight - 30);
  pdf.setLineWidth(1);
  pdf.rect(20, 20, pageWidth - 40, pageHeight - 40);

  // Certificate title
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(42);
  pdf.setTextColor(99, 102, 241);
  pdf.text('CERTIFICATE', pageWidth / 2, 55, { align: 'center' });

  pdf.setFontSize(18);
  pdf.setTextColor(107, 114, 128);
  pdf.text('OF COMPLETION', pageWidth / 2, 65, { align: 'center' });

  // Decorative line
  pdf.setDrawColor(99, 102, 241);
  pdf.setLineWidth(0.5);
  pdf.line(pageWidth / 2 - 50, 72, pageWidth / 2 + 50, 72);

  // "This certifies that"
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(14);
  pdf.setTextColor(107, 114, 128);
  pdf.text('This is to certify that', pageWidth / 2, 90, { align: 'center' });

  // Student name
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(32);
  pdf.setTextColor(31, 41, 55);
  pdf.text(studentName, pageWidth / 2, 108, { align: 'center' });

  // Decorative line under name
  pdf.setDrawColor(99, 102, 241);
  pdf.line(pageWidth / 2 - 60, 115, pageWidth / 2 + 60, 115);

  // "has successfully completed"
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(14);
  pdf.setTextColor(107, 114, 128);
  pdf.text('has successfully completed the course', pageWidth / 2, 130, { align: 'center' });

  // Course name
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(24);
  pdf.setTextColor(99, 102, 241);
  const courseLines = pdf.splitTextToSize(courseName, pageWidth - 80);
  let courseY = 145;
  courseLines.forEach((line: string) => {
    pdf.text(line, pageWidth / 2, courseY, { align: 'center' });
    courseY += 10;
  });

  // Date
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(12);
  pdf.setTextColor(107, 114, 128);
  pdf.text(`Completed on: ${completionDate}`, pageWidth / 2, courseY + 15, { align: 'center' });

  // Signature line and instructor
  const sigY = pageHeight - 55;

  // Left signature (Instructor)
  pdf.setDrawColor(156, 163, 175);
  pdf.line(50, sigY, 120, sigY);
  pdf.setFontSize(10);
  pdf.text(instructorName, 85, sigY + 8, { align: 'center' });
  pdf.setFontSize(9);
  pdf.setTextColor(156, 163, 175);
  pdf.text('Instructor', 85, sigY + 14, { align: 'center' });

  // Right signature (Brand)
  pdf.setDrawColor(156, 163, 175);
  pdf.line(pageWidth - 120, sigY, pageWidth - 50, sigY);
  pdf.setFontSize(10);
  pdf.setTextColor(107, 114, 128);
  pdf.text(brandName, pageWidth - 85, sigY + 8, { align: 'center' });
  pdf.setFontSize(9);
  pdf.setTextColor(156, 163, 175);
  pdf.text('Issued By', pageWidth - 85, sigY + 14, { align: 'center' });

  // Certificate ID
  const certId = `CERT-${Date.now().toString(36).toUpperCase()}`;
  pdf.setFontSize(8);
  pdf.setTextColor(156, 163, 175);
  pdf.text(`Certificate ID: ${certId}`, pageWidth / 2, pageHeight - 35, { align: 'center' });

  // Save the PDF
  const fileName = `certificate_${studentName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${courseName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
  pdf.save(fileName);
};

// Export course in Teachable-compatible CSV format
export const exportToTeachable = (course: any) => {
  // Teachable bulk upload format
  const rows: string[][] = [];

  // Header row
  rows.push([
    'Section Name',
    'Lecture Name',
    'Lecture Type',
    'Content',
    'Duration (minutes)',
    'Free Preview'
  ]);

  // Add course data
  course.modules?.forEach((module: any) => {
    module.lessons?.forEach((lesson: any, lessonIndex: number) => {
      const content = lesson.content || {};
      const fullContent = [
        content.introduction || '',
        content.mainContent || '',
        (content.keyTakeaways || []).join('\n'),
        (content.exercises || []).join('\n'),
        content.videoScript || ''
      ].filter(Boolean).join('\n\n');

      rows.push([
        module.title,
        lesson.title,
        lesson.contentType === 'video' ? 'Video' : 'Text',
        fullContent,
        String(lesson.duration || 10),
        lessonIndex === 0 ? 'Yes' : 'No'
      ]);
    });

    // Add quiz as a lecture if exists
    if (module.quiz?.questions) {
      const quizContent = module.quiz.questions.map((q: any, idx: number) =>
        `Q${idx + 1}: ${q.question}\n` +
        q.options.map((opt: string) => `  ${opt}`).join('\n') +
        `\nAnswer: ${q.correctAnswer}\nExplanation: ${q.explanation}`
      ).join('\n\n');

      rows.push([
        module.title,
        `${module.title} - Quiz`,
        'Quiz',
        quizContent,
        '15',
        'No'
      ]);
    }
  });

  // Convert to CSV
  const csvContent = rows.map(row =>
    row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(',')
  ).join('\n');

  // Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${course.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_teachable.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Export course in Thinkific-compatible JSON format
export const exportToThinkific = (course: any) => {
  const thinkificCourse = {
    name: course.title,
    description: course.description,
    chapters: course.modules?.map((module: any, moduleIndex: number) => ({
      name: module.title,
      description: module.description,
      position: moduleIndex + 1,
      contents: [
        // Lessons
        ...module.lessons?.map((lesson: any, lessonIndex: number) => {
          const content = lesson.content || {};
          return {
            name: lesson.title,
            description: lesson.description,
            content_type: lesson.contentType === 'video' ? 'video' : 'text',
            position: lessonIndex + 1,
            free: lessonIndex === 0,
            html_content: `
              <h2>Introduction</h2>
              <p>${content.introduction || ''}</p>
              <h2>Main Content</h2>
              <p>${(content.mainContent || '').replace(/\n/g, '</p><p>')}</p>
              ${content.keyTakeaways?.length ? `
                <h2>Key Takeaways</h2>
                <ul>${content.keyTakeaways.map((t: string) => `<li>${t}</li>`).join('')}</ul>
              ` : ''}
              ${content.exercises?.length ? `
                <h2>Exercises</h2>
                <ul>${content.exercises.map((e: string) => `<li>${e}</li>`).join('')}</ul>
              ` : ''}
              ${content.videoScript ? `
                <h2>Video Script</h2>
                <p>${content.videoScript.replace(/\n/g, '</p><p>')}</p>
              ` : ''}
            `,
            duration_in_minutes: lesson.duration || 10
          };
        }) || [],
        // Quiz
        ...(module.quiz?.questions ? [{
          name: `${module.title} Quiz`,
          content_type: 'quiz',
          position: (module.lessons?.length || 0) + 1,
          questions: module.quiz.questions.map((q: any, idx: number) => ({
            text: q.question,
            position: idx + 1,
            answers: q.options.map((opt: string, optIdx: number) => ({
              text: opt.replace(/^[A-D]\)\s*/, ''),
              correct: opt.startsWith(q.correctAnswer),
              position: optIdx + 1
            })),
            explanation: q.explanation
          }))
        }] : [])
      ]
    })) || [],
    instructor: {
      bio: 'Course created with Templimize AI'
    },
    meta: {
      target_audience: course.targetAudience,
      learning_objectives: course.learningObjectives || [],
      niche: course.niche,
      exported_at: new Date().toISOString(),
      source: 'templimize_ai'
    }
  };

  // Download as JSON
  const jsonContent = JSON.stringify(thinkificCourse, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${course.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_thinkific.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface WhiteLabelOptions {
  brandName?: string;
  removeBranding?: boolean;
}

// Export course to PDF with white-label option
export const exportCourseToPDFWhiteLabel = (course: any, options: WhiteLabelOptions = {}) => {
  const { brandName, removeBranding = false } = options;

  const pdf = new jsPDF();
  let yPos = 20;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);

  // Helper function to add text with page break handling
  const addText = (text: string, fontSize: number, isBold: boolean = false, color: [number, number, number] = [0, 0, 0]) => {
    if (!text) return;
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
    pdf.setTextColor(color[0], color[1], color[2]);

    const lines = pdf.splitTextToSize(text, contentWidth);
    const lineHeight = fontSize * 0.45;

    lines.forEach((line: string) => {
      if (yPos + lineHeight > pageHeight - 20) {
        pdf.addPage();
        yPos = 20;
      }
      pdf.text(line, margin, yPos);
      yPos += lineHeight;
    });
  };

  // Title
  addText(course.title, 24, true, [99, 102, 241]);
  yPos += 5;

  // Brand name (if not white-label)
  if (!removeBranding) {
    addText(brandName ? `Created with ${brandName}` : 'Created with Templimize AI', 10, false, [156, 163, 175]);
  }
  yPos += 10;

  // Description
  addText(course.description, 12, false);
  yPos += 10;

  // Modules and lessons
  course.modules?.forEach((module: any, moduleIdx: number) => {
    if (yPos > pageHeight - 60) {
      pdf.addPage();
      yPos = 20;
    }

    addText(`Module ${moduleIdx + 1}: ${module.title}`, 16, true, [79, 70, 229]);
    yPos += 3;
    addText(module.description, 11, false, [75, 85, 99]);
    yPos += 8;

    module.lessons?.forEach((lesson: any, lessonIdx: number) => {
      addText(`  Lesson ${lessonIdx + 1}: ${lesson.title}`, 13, true);

      if (lesson.content) {
        if (lesson.content.introduction) {
          addText(`    ${lesson.content.introduction}`, 10, false, [75, 85, 99]);
        }
        if (lesson.content.mainContent) {
          addText(`    ${lesson.content.mainContent.substring(0, 500)}...`, 10, false, [75, 85, 99]);
        }
        if (lesson.content.keyTakeaways?.length) {
          addText('    Key Takeaways:', 10, true);
          lesson.content.keyTakeaways.forEach((t: string) => {
            addText(`      - ${t}`, 10, false, [75, 85, 99]);
          });
        }
      }
      yPos += 5;
    });

    yPos += 5;
  });

  // Footer (only if not white-label)
  if (!removeBranding) {
    pdf.setFontSize(8);
    pdf.setTextColor(156, 163, 175);
    pdf.text(
      brandName || 'Powered by Templimize AI - The Course Creator\'s Vault',
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }

  // Save
  const fileName = `${course.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_course${removeBranding ? '_whitelabel' : ''}.pdf`;
  pdf.save(fileName);
};
